import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Ban, Search, User, ShieldOff, Calendar, AlertTriangle, Trash2, Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestioneBan = () => {
  const { user, isSindacatoAdmin, hasPermesso } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [oreDialogOpen, setOreDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banForm, setBanForm] = useState({ motivo: '', durata_giorni: null, permanente: true });
  const [warnForm, setWarnForm] = useState({ motivo: '' });
  const [oreForm, setOreForm] = useState({ ore: 0, minuti: 0 });

  const isAdmin = isSindacatoAdmin();
  const canBan = isAdmin || hasPermesso('gestisci_ban');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/all-users-for-ban`);
      setAllUsers(res.data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(u =>
    u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.cognome?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separa utenti bannati e attivi
  const bannedUsers = filteredUsers.filter(u => u.ban_info?.is_banned);
  const activeUsers = filteredUsers.filter(u => !u.ban_info?.is_banned);
  const usersWithWarns = filteredUsers.filter(u => u.warns?.length > 0);

  const openBanDialog = (u) => {
    setSelectedUser(u);
    setBanForm({ motivo: '', durata_giorni: null, permanente: true });
    setBanDialogOpen(true);
  };

  const openWarnDialog = (u) => {
    setSelectedUser(u);
    setWarnForm({ motivo: '' });
    setWarnDialogOpen(true);
  };

  const handleBan = async () => {
    if (!banForm.motivo.trim()) {
      toast.error('Inserisci un motivo per il ban');
      return;
    }
    try {
      await axios.post(`${API_URL}/ban/${selectedUser.id}`, {
        motivo: banForm.motivo,
        durata_giorni: banForm.permanente ? null : banForm.durata_giorni
      });
      toast.success(`Utente @${selectedUser.username} bannato`);
      setBanDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ban');
    }
  };

  const handleUnban = async (userId, username) => {
    if (!confirm(`Sei sicuro di voler rimuovere il ban a @${username}?`)) return;
    try {
      await axios.delete(`${API_URL}/ban/${userId}`);
      toast.success(`Ban rimosso per @${username}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione del ban');
    }
  };

  const handleWarn = async () => {
    if (!warnForm.motivo.trim()) {
      toast.error('Inserisci un motivo per il warn');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/warn`, {
        user_id: selectedUser.id,
        motivo: warnForm.motivo
      });
      toast.success(res.data.message || `Warn dato a @${selectedUser.username}`);
      setWarnDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel dare warn');
    }
  };

  const handleRemoveWarn = async (userId, warnId, username) => {
    if (!confirm(`Sei sicuro di voler rimuovere questo warn?`)) return;
    try {
      await axios.delete(`${API_URL}/warn/${userId}/${warnId}`);
      toast.success(`Warn rimosso per @${username}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione del warn');
    }
  };

  const openOreDialog = (u) => {
    setSelectedUser(u);
    setOreForm({ ore: 0, minuti: 0 });
    setOreDialogOpen(true);
  };

  const handleRegistraOre = async () => {
    if (oreForm.ore === 0 && oreForm.minuti === 0) {
      toast.error('Inserisci almeno un valore per ore o minuti');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/ore-lavoro/registra`, {
        user_id: selectedUser.id,
        ore: parseInt(oreForm.ore) || 0,
        minuti: parseInt(oreForm.minuti) || 0
      });
      toast.success(res.data.message || `Ore registrate per @${selectedUser.username}`);
      setOreDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione ore');
    }
  };

  if (!canBan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Ban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Non hai i permessi per gestire i ban</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="gestione-ban-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-header flex items-center gap-3">
          <Ban className="w-8 h-8 text-red-600" />
          Gestione Ban e Warn
        </h1>
        <p className="text-slate-600 mt-1">
          Gestisci i ban e i warn degli utenti dal sistema
        </p>
      </div>

      {/* Barra di ricerca */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca utente per nickname, nome o cognome..."
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="ban" className="w-full">
        <TabsList>
          <TabsTrigger value="ban" className="flex items-center gap-2">
            <Ban className="w-4 h-4" />
            Gestione Ban
          </TabsTrigger>
          <TabsTrigger value="warn" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Gestione Warn
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="ore" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Ore Online
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab Ban */}
        <TabsContent value="ban" className="mt-6 space-y-6">
          {/* Sezione Utenti Bannati */}
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Utenti Bannati ({bannedUsers.length})
              </CardTitle>
              <CardDescription>Utenti attualmente bannati dal sistema</CardDescription>
            </CardHeader>
            <CardContent>
              {bannedUsers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun utente bannato</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utente</TableHead>
                        <TableHead>Bannato da</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Durata</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bannedUsers.map((u) => (
                        <TableRow key={u.id} className="bg-red-50/50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-red-600" />
                              </div>
                              <div>
                                <span className="font-medium">@{u.username}</span>
                                <p className="text-xs text-slate-500">{u.nome} {u.cognome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-red-700">
                              {u.ban_info?.banned_by_nome || 'Staff'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm italic max-w-xs truncate block">
                              {u.ban_info?.motivo || 'Non specificato'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {u.ban_info?.permanente || !u.ban_info?.data_scadenza ? (
                              <Badge variant="destructive">Permanente</Badge>
                            ) : (
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="w-3 h-3" />
                                <span>Fino al {new Date(u.ban_info.data_scadenza).toLocaleDateString('it-IT')}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleUnban(u.id, u.username)}
                              >
                                <ShieldOff className="w-4 h-4 mr-1" />
                                Sbanna
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sezione Utenti Attivi */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Utenti Attivi ({activeUsers.length})
              </CardTitle>
              <CardDescription>Utenti che possono accedere al sistema</CardDescription>
            </CardHeader>
            <CardContent>
              {activeUsers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun utente trovato</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utente</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Warn</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-600" />
                              </div>
                              <div>
                                <span className="font-medium">@{u.username}</span>
                                <p className="text-xs text-slate-500">{u.nome} {u.cognome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {u.ruolo === 'SINDACATO_ADMIN' ? 'Admin' : u.ruolo || 'Cittadino'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {u.warns?.length > 0 ? (
                              <Badge className="bg-amber-100 text-amber-700">{u.warns.length} warn</Badge>
                            ) : (
                              <span className="text-slate-400 text-sm">Nessuno</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN' && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-600 hover:bg-amber-50"
                                  onClick={() => openWarnDialog(u)}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />
                                  Warn
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => openBanDialog(u)}
                                >
                                  <Ban className="w-4 h-4 mr-1" />
                                  Banna
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Warn */}
        <TabsContent value="warn" className="mt-6 space-y-6">
          {/* Riepilogo Warn */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-amber-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-700">{usersWithWarns.length}</p>
                <p className="text-sm text-amber-600">Utenti con Warn</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-700">
                  {allUsers.reduce((sum, u) => sum + (u.warns?.length || 0), 0)}
                </p>
                <p className="text-sm text-red-600">Warn Totali</p>
              </CardContent>
            </Card>
          </div>

          {/* Lista Utenti con Warn */}
          <Card className="border-amber-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  Utenti con Warn ({usersWithWarns.length})
                </CardTitle>
                <CardDescription>Gestisci i warn degli utenti</CardDescription>
              </div>
              <Button 
                onClick={() => {
                  setSelectedUser(null);
                  setWarnForm({ motivo: '' });
                  setWarnDialogOpen(true);
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Dai Nuovo Warn
              </Button>
            </CardHeader>
            <CardContent>
              {usersWithWarns.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessun utente con warn attivi</p>
              ) : (
                <div className="space-y-4">
                  {usersWithWarns.map((u) => (
                    <div key={u.id} className="border rounded-lg p-4 bg-amber-50/30">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-700" />
                          </div>
                          <div>
                            <p className="font-semibold">@{u.username}</p>
                            <p className="text-sm text-slate-500">{u.nome} {u.cognome}</p>
                          </div>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">{u.warns.length} warn</Badge>
                      </div>
                      <div className="space-y-2">
                        {u.warns.map((w, idx) => (
                          <div key={w.id || idx} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-amber-800">{w.motivo}</p>
                              <p className="text-xs text-slate-500">
                                Dato da @{w.dato_da_username || 'Staff'} - {w.created_at ? new Date(w.created_at).toLocaleDateString('it-IT') : 'N/A'}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:bg-red-50"
                              onClick={() => handleRemoveWarn(u.id, w.id, u.username)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 border-amber-200"
                          onClick={() => openWarnDialog(u)}
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Aggiungi Warn
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ore Online (Solo Admin) */}
        {isAdmin && (
          <TabsContent value="ore" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-cyan-700">
                    <Clock className="w-5 h-5" />
                    Gestione Ore Online
                  </CardTitle>
                  <CardDescription>Registra ore online per gli utenti</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utente</TableHead>
                        <TableHead className="text-center">Ore Settimanali</TableHead>
                        <TableHead className="text-center">Ore Totali</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.filter(u => u.ruolo !== 'SINDACATO_ADMIN').map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-cyan-600" />
                              </div>
                              <div>
                                <p className="font-medium">@{u.username}</p>
                                <p className="text-xs text-slate-500">{u.nome} {u.cognome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-cyan-100 text-cyan-700">
                              {u.ore_lavoro_settimanali?.ore || 0}h {u.ore_lavoro_settimanali?.minuti || 0}m
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-700">
                              {u.ore_lavoro_totali?.ore || 0}h {u.ore_lavoro_totali?.minuti || 0}m
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => openOreDialog(u)}
                              className="bg-cyan-600 hover:bg-cyan-700"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Aggiungi Ore
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog Ban */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Banna Utente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-600">Stai per bannare:</p>
              <p className="font-semibold">@{selectedUser?.username}</p>
              <p className="text-xs text-slate-500">{selectedUser?.nome} {selectedUser?.cognome}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo del Ban *</Label>
              <Textarea
                value={banForm.motivo}
                onChange={(e) => setBanForm({ ...banForm, motivo: e.target.value })}
                placeholder="Inserisci il motivo del ban..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Durata</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={banForm.permanente}
                    onChange={() => setBanForm({ ...banForm, permanente: true, durata_giorni: null })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Permanente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!banForm.permanente}
                    onChange={() => setBanForm({ ...banForm, permanente: false })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Temporaneo</span>
                </label>
              </div>
              {!banForm.permanente && (
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="1"
                    value={banForm.durata_giorni || ''}
                    onChange={(e) => setBanForm({ ...banForm, durata_giorni: parseInt(e.target.value) || null })}
                    placeholder="Numero giorni"
                    className="w-32"
                  />
                  <span className="text-sm text-slate-500">giorni</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setBanDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleBan}
                className="bg-red-600 hover:bg-red-700"
                disabled={!banForm.motivo.trim()}
              >
                <Ban className="w-4 h-4 mr-2" />
                Conferma Ban
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Warn */}
      <Dialog open={warnDialogOpen} onOpenChange={setWarnDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Dai Warn
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedUser ? (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700">Stai per dare un warn a:</p>
                <p className="font-semibold">@{selectedUser.username}</p>
                <p className="text-xs text-slate-500">{selectedUser.nome} {selectedUser.cognome}</p>
                {selectedUser.warns?.length > 0 && (
                  <Badge className="mt-1 bg-amber-100 text-amber-700">{selectedUser.warns.length} warn precedenti</Badge>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Seleziona Utente *</Label>
                <select 
                  className="w-full p-2 border rounded-lg"
                  onChange={(e) => {
                    const u = allUsers.find(user => user.id === e.target.value);
                    setSelectedUser(u);
                  }}
                  value={selectedUser?.id || ''}
                >
                  <option value="">Seleziona un utente...</option>
                  {allUsers.filter(u => u.id !== user?.id && u.ruolo !== 'SINDACATO_ADMIN').map(u => (
                    <option key={u.id} value={u.id}>
                      @{u.username} - {u.nome} {u.cognome} {u.warns?.length > 0 ? `(${u.warns.length} warn)` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Motivo del Warn *</Label>
              <Textarea
                value={warnForm.motivo}
                onChange={(e) => setWarnForm({ ...warnForm, motivo: e.target.value })}
                placeholder="Inserisci il motivo del warn..."
                rows={3}
              />
              <p className="text-xs text-slate-500">
                L'utente vedrà: "Sei stato warnato da [tuo nome] per: [motivo]"
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setWarnDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleWarn}
                className="bg-amber-600 hover:bg-amber-700"
                disabled={!warnForm.motivo.trim() || !selectedUser}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Conferma Warn
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ore Online */}
      <Dialog open={oreDialogOpen} onOpenChange={setOreDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-600 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Registra Ore Online
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedUser && (
              <div className="p-3 bg-cyan-50 rounded-lg">
                <p className="text-sm text-cyan-700">Stai registrando ore per:</p>
                <p className="font-semibold">@{selectedUser.username}</p>
                <p className="text-xs text-slate-500">{selectedUser.nome} {selectedUser.cognome}</p>
                <div className="mt-2 flex gap-4 text-xs">
                  <span className="text-cyan-600">
                    Settimanali: {selectedUser.ore_lavoro_settimanali?.ore || 0}h {selectedUser.ore_lavoro_settimanali?.minuti || 0}m
                  </span>
                  <span className="text-blue-600">
                    Totali: {selectedUser.ore_lavoro_totali?.ore || 0}h {selectedUser.ore_lavoro_totali?.minuti || 0}m
                  </span>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ore</Label>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  value={oreForm.ore}
                  onChange={(e) => setOreForm({ ...oreForm, ore: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Minuti</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={oreForm.minuti}
                  onChange={(e) => setOreForm({ ...oreForm, minuti: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500">
              Le ore verranno aggiunte sia al conteggio settimanale che al totale.
            </p>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setOreDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleRegistraOre}
                className="bg-cyan-600 hover:bg-cyan-700"
                disabled={oreForm.ore === 0 && oreForm.minuti === 0}
              >
                <Clock className="w-4 h-4 mr-2" />
                Registra Ore
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestioneBan;
